import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BACKEND, TOKEN_KEY } from "@/lib/api";

// Subscribes to the tenant WebSocket and refreshes React Query caches on live events.
// Falls back gracefully (existing polling intervals keep data fresh if WS is unavailable).
export function useRealtime() {
  const qc = useQueryClient();
  const wsRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || !BACKEND) return;
    let closed = false;
    let reconnectTimer;

    const connect = () => {
      try {
        const wsBase = BACKEND.replace(/^http/, "ws");
        const ws = new WebSocket(`${wsBase}/api/ws?token=${token}`);
        wsRef.current = ws;

        ws.onmessage = (e) => {
          let msg;
          try { msg = JSON.parse(e.data); } catch { return; }
          qc.invalidateQueries();
          if (msg.type === "payment_received") {
            toast.success(`💳 Payment received · ${msg.invoice_number}`, {
              description: `₹${Number(msg.amount || 0).toLocaleString("en-IN")}`,
            });
          } else if (msg.type === "lead_created") {
            toast.info(`🎯 New lead: ${msg.name || "Unnamed"}`);
          } else if (msg.type === "ticket_created") {
            toast.info(`🎫 New ticket: ${msg.subject || ""}`);
          } else if (msg.type === "quote_accepted") {
            toast.success("✅ A quotation was accepted");
          }
        };
        ws.onclose = () => { if (!closed) reconnectTimer = setTimeout(connect, 4000); };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) {
        if (!closed) reconnectTimer = setTimeout(connect, 4000);
      }
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch (_) {}
    };
  }, [qc]);
}
